import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Search, X } from "lucide-react";
import { Reveal, Overline } from "@/components/landing/primitives";
import { ARTICLES, ARTICLES_BY_SLUG, RELATED_TREATMENTS, DISCLAIMER, SIGNATURE } from "@/content/approfondimenti";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

const EXPLORE_TREATMENTS = [
  { label: "Trattamenti laser", to: "/trattamenti-laser" },
  { label: "Cicatrici", to: "/cicatrici" },
  { label: "Vascolare", to: "/vascolare" },
  { label: "Macchie e discromie", to: "/trattamenti-laser/macchie-discromie" },
];

function NotFound() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
      <p className="font-serif text-5xl">Approfondimento non trovato</p>
      <Link to="/approfondimenti" className="link-underline text-sm">Torna agli approfondimenti</Link>
    </div>
  );
}

export function ApprofondimentiPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = q
    ? ARTICLES.filter((p) =>
        [p.title, p.metaDescription, p.intro].filter(Boolean).join(" ").toLowerCase().includes(q)
      )
    : ARTICLES;
  return (
    <div className="bg-canvas text-ink">
      <section className="pt-36 md:pt-44 pb-14">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal>
            <Link to="/" className="inline-flex items-center gap-2 text-sm opacity-60 hover:opacity-100 link-underline mb-10">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
          </Reveal>
          <Reveal><Overline>Approfondimenti</Overline></Reveal>
          <Reveal delay={0.1} as="h1" className="mt-7 font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight max-w-4xl">
            Domande frequenti dei pazienti, risposte del medico.
          </Reveal>
          <Reveal delay={0.18} as="p" className="mt-8 text-lg text-muted leading-relaxed max-w-2xl">
            Approfondimenti dello studio del Dott. Alessandro Valle a Genova: spieghiamo con linguaggio chiaro e scientificamente corretto come funzionano i trattamenti, quali sono le indicazioni e cosa aspettarsi.
          </Reveal>
        </div>
      </section>

      <section className="pb-16 md:pb-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="mb-10">
            <div className="relative max-w-xl">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="approfondimenti-search"
                placeholder="Cerca un argomento: capelli, macchie, acido ialuronico…"
                className="w-full bg-canvas border border-ink/15 pl-11 pr-11 py-3.5 text-[15px] text-ink placeholder:text-ink/40 focus:outline-none focus:border-sagedark transition-colors duration-300"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  data-testid="approfondimenti-search-clear"
                  aria-label="Cancella ricerca"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-ink/40 hover:text-ink transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="mt-3 text-[13px] text-muted" data-testid="approfondimenti-search-count">
              {q
                ? `${filtered.length} ${filtered.length === 1 ? "approfondimento trovato" : "approfondimenti trovati"} per "${query.trim()}"`
                : `${ARTICLES.length} approfondimenti disponibili`}
            </p>
          </div>

          {filtered.length === 0 ? (
            <div className="border border-ink/10 bg-canvas py-20 text-center" data-testid="approfondimenti-empty">
              <p className="font-serif text-2xl text-ink">Nessun approfondimento trovato</p>
              <p className="mt-3 text-sm text-muted">Prova con un altro termine oppure prenota una valutazione per una risposta personalizzata.</p>
              <button onClick={() => setQuery("")} className="mt-6 border border-ink/20 px-6 py-2.5 text-xs uppercase tracking-[0.15em] text-ink/70 hover:border-sagedark hover:text-sagedark transition-colors">Mostra tutti</button>
            </div>
          ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
            {filtered.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.06}>
                <div className="flex flex-col h-full bg-canvas">
                  <div className="relative overflow-hidden h-52">
                    <img src={p.cover} alt={p.coverAlt} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <span className="text-[11px] uppercase tracking-[0.15em] text-sagedark">{formatDate(p.datePublished)}</span>
                    <h2 className="mt-3 font-serif text-2xl leading-tight">{p.title}</h2>
                    <p className="mt-3 text-sm text-muted leading-relaxed line-clamp-3 flex-1">{p.metaDescription}</p>
                    <Link to={`/approfondimenti/${p.slug}`} data-testid={`approfondimento-card-${p.slug}`} className="mt-6 group relative inline-flex w-fit items-center overflow-hidden border border-ink px-6 py-3 text-xs uppercase tracking-[0.15em]">
                      <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
                      <span className="relative group-hover:text-canvas transition-colors duration-500 inline-flex items-center gap-2">Leggi l'approfondimento <ArrowUpRight className="w-4 h-4" /></span>
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          )}

          <div className="mt-14">
            <Overline>Vai ai trattamenti</Overline>
            <div className="mt-5 flex flex-wrap gap-3">
              {EXPLORE_TREATMENTS.map((t) => (
                <Link key={t.to} to={t.to} className="border border-ink/15 px-4 py-2 text-[13px] text-ink/70 hover:border-sage hover:text-ink transition-colors duration-300">{t.label}</Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-sand">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 text-center">
          <Reveal as="h2" className="font-serif font-light text-3xl md:text-5xl leading-tight tracking-tight max-w-3xl mx-auto">
            Ogni percorso parte da una valutazione medica individuale.
          </Reveal>
          <Reveal delay={0.1} as="div" className="mt-9">
            <button onClick={() => navigate("/contatti")} data-testid="approfondimenti-cta" className="group relative inline-flex items-center overflow-hidden border border-ink px-10 py-4 text-xs uppercase tracking-[0.2em]">
              <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <span className="relative group-hover:text-canvas transition-colors duration-500">Prenota una valutazione</span>
            </button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = ARTICLES_BY_SLUG[slug];
  if (!post) return <NotFound />;
  const others = ARTICLES.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div className="bg-canvas text-ink">
      <article>
        <section className="pt-36 md:pt-44 pb-10">
          <div className="max-w-[820px] mx-auto px-6 md:px-10">
            <Reveal>
              <div className="flex items-center gap-2 text-sm text-muted mb-10">
                <Link to="/" className="hover:text-ink link-underline">Home</Link>
                <span>/</span>
                <Link to="/approfondimenti" className="hover:text-ink link-underline">Approfondimenti</Link>
              </div>
            </Reveal>
            <Reveal><Overline>Approfondimento</Overline></Reveal>
            <Reveal delay={0.1} as="h1" className="mt-6 font-serif font-light text-4xl md:text-5xl leading-[1.08] tracking-tight">{post.title}</Reveal>
            <Reveal delay={0.18} as="p" className="mt-7 text-xl text-muted leading-relaxed">{post.intro}</Reveal>
          </div>
        </section>

        <section className="pb-14">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <div className="relative overflow-hidden h-[38vh] md:h-[54vh] ring-1 ring-inset ring-ink/10">
              <img src={post.cover} alt={post.coverAlt} className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
        </section>

        <section className="pb-8">
          <div className="max-w-[720px] mx-auto px-6 md:px-10">
            {post.body.map((b, i) => {
              if (b.type === "h2") return <Reveal key={i} as="h2" className="mt-12 mb-4 font-serif text-2xl md:text-3xl leading-tight">{b.text}</Reveal>;
              if (b.type === "h3") return <Reveal key={i} as="h3" className="mt-8 mb-3 font-serif text-xl leading-tight text-ink/90">{b.text}</Reveal>;
              if (b.type === "ul")
                return (
                  <ul key={i} className="my-5 space-y-2.5">
                    {b.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-3 text-[16px] text-ink/80 leading-relaxed">
                        <span className="text-sage mt-1.5 shrink-0">—</span>{it}
                      </li>
                    ))}
                  </ul>
                );
              return <Reveal key={i} as="p" className="my-5 text-[17px] leading-[1.75] text-ink/85">{b.text}</Reveal>;
            })}
          </div>
        </section>

        {/* In sintesi */}
        {post.inSintesi && post.inSintesi.length > 0 && (
          <section className="pb-12">
            <div className="max-w-[720px] mx-auto px-6 md:px-10">
              <div className="bg-sand border-l-2 border-sage p-6 md:p-8">
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted mb-4">In sintesi</p>
                <ul className="space-y-2.5">
                  {post.inSintesi.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px] text-ink/80 leading-relaxed">
                      <span className="text-sage mt-1.5 shrink-0">—</span>{s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* FAQ */}
        {post.faqs && post.faqs.length > 0 && (
          <section className="pb-12">
            <div className="max-w-[720px] mx-auto px-6 md:px-10">
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted mb-4">Domande frequenti</p>
              <div className="border-t border-ink/15">
                {post.faqs.map((f, i) => (
                  <details key={i} data-testid={`article-faq-${i}`} className="group border-b border-ink/15">
                    <summary className="flex items-start justify-between gap-6 cursor-pointer list-none py-5 select-none">
                      <span className="font-serif text-lg md:text-xl leading-snug">{f.q}</span>
                    </summary>
                    <p className="pb-5 -mt-1 text-[15px] leading-relaxed text-muted">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Collegamenti interni + CTA + firma */}
        <section className="pb-16">
          <div className="max-w-[720px] mx-auto px-6 md:px-10">
            {(RELATED_TREATMENTS[post.slug] || []).length > 0 && (
              <div className="mb-10 bg-sand border-l-2 border-sage p-6 md:p-7" data-testid="related-treatments">
                <p className="text-[11px] uppercase tracking-[0.2em] text-sagedark mb-4">{(RELATED_TREATMENTS[post.slug] || []).length === 1 ? "Trattamento correlato" : "Trattamenti correlati"}</p>
                <div className="divide-y divide-ink/10 border-y border-ink/10">
                  {(RELATED_TREATMENTS[post.slug] || []).map((t) => (
                    <Link key={t.to} to={t.to} data-testid={`related-treatment-${t.to}`} className="group flex items-center justify-between py-4 hover:px-2 transition-[padding] duration-300">
                      <span className="font-serif text-lg md:text-xl">{t.label}</span>
                      <ArrowRight className="w-5 h-5 text-ink/40 group-hover:text-ink group-hover:translate-x-1 transition-all duration-300" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {post.internalLinks && post.internalLinks.length > 0 && (
              <div className="mb-10">
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted mb-4">Approfondisci i trattamenti</p>
                <div className="flex flex-wrap gap-3">
                  {post.internalLinks.map((t) => (
                    <Link key={t.to} to={t.to} data-testid={`article-link-${t.to}`} className="border border-ink/15 px-4 py-2 text-[13px] text-ink/70 hover:border-sage hover:text-ink transition-colors duration-300">{t.label}</Link>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-graphite text-canvas p-7 md:p-9">
              <p className="font-serif text-2xl md:text-3xl leading-snug">Vuoi capire quale percorso è adatto al tuo caso?</p>
              <p className="mt-3 text-sm text-canvas/60 leading-relaxed">Ogni trattamento viene definito dopo una valutazione medica individuale.</p>
              <div className="mt-7 flex flex-wrap gap-4">
                <button onClick={() => navigate("/contatti")} data-testid="article-cta" className="group relative inline-flex items-center overflow-hidden border border-canvas/40 px-8 py-3.5 text-xs uppercase tracking-[0.2em]">
                  <span className="absolute inset-0 bg-canvas translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
                  <span className="relative group-hover:text-graphite transition-colors duration-500">Prenota una valutazione</span>
                </button>
                <Link to="/dott-alessandro-valle" data-testid="article-doctor-link" className="inline-flex items-center gap-2 text-sm text-canvas/75 hover:text-canvas link-underline">
                  Chi è il Dott. Valle <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Firma + date */}
            <div className="mt-10 border-t border-ink/10 pt-6">
              <p className="font-serif text-lg">{SIGNATURE}</p>
              <p className="mt-2 text-xs text-muted">
                Pubblicato il {formatDate(post.datePublished)} · Ultima revisione: {formatDate(post.dateModified)}
              </p>
            </div>

            {/* Fonti */}
            {post.sources && post.sources.length > 0 && (
              <div className="mt-8">
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted mb-3">Fonti</p>
                <ul className="space-y-2">
                  {post.sources.map((s, i) => (
                    <li key={i} className="text-sm">
                      <a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-ink/70 hover:text-ink link-underline">{s.label}</a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Disclaimer */}
            <p className="mt-8 text-xs text-muted leading-relaxed border-t border-ink/10 pt-6">{DISCLAIMER}</p>

            <div className="mt-8">
              <Link to="/approfondimenti" className="inline-flex items-center gap-2 text-sm text-ink/70 hover:text-ink link-underline">
                <ArrowLeft className="w-4 h-4" /> Tutti gli approfondimenti
              </Link>
            </div>
          </div>
        </section>
      </article>

      {others.length > 0 && (
        <section className="py-16 md:py-24 bg-sand">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10">
            <Overline>Altri approfondimenti</Overline>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10 border border-ink/10">
              {others.map((p) => (
                <Link key={p.slug} to={`/approfondimenti/${p.slug}`} className="group bg-canvas p-7 hover:bg-stone transition-colors duration-500">
                  <span className="text-[11px] uppercase tracking-[0.15em] text-sagedark">{formatDate(p.datePublished)}</span>
                  <h3 className="mt-3 font-serif text-xl leading-tight">{p.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
